import { useRef, useState } from 'react'
import { AlertTriangle, Check, RefreshCw, ShieldAlert, XCircle } from 'lucide-react'
import type { RelatieControle, RelatieGegevens, RelatieVelden } from '../../../gedeeld/types'
import { c } from '../../lib/api'
import { meldFout } from '../../lib/staat'
import { Knop } from '../ui'

/* ===========================================================================
 *  Een relatie voor Exact, op de kaart van een voorstel
 *
 *  De AI las dit meestal van een factuur. Dus: alles open, en wat de server
 *  bij het voorstel zag (bestaat hij al, klopt het IBAN, wat zegt VIES)
 *  erbij. Verander je iets, dan klopt die controle niet meer; "Opnieuw
 *  controleren" vraagt hem opnieuw. Bij Uitvoeren kijkt de server het hoe
 *  dan ook nog een keer na -- deze controle is om het te ZIEN, niet om erop
 *  te vertrouwen.
 *
 *  Bestaat hij al in een bv, dan wordt hij daar overgeslagen, tenzij je
 *  "toch aanmaken" aanvinkt.
 * =========================================================================== */

export const EXACT_URL = 'https://exact.truckwash-workspace.com'

const SOORTEN: { key: RelatieGegevens['soort']; label: string }[] = [
  { key: 'leverancier', label: 'Leverancier' },
  { key: 'klant', label: 'Klant' },
  { key: 'beide', label: 'Beide' },
]

const OP: Record<string, string> = { btw: 'btw-nummer', kvk: 'KvK-nummer', naam: 'naam', iban: 'IBAN' }

export default function RelatieFormulier({ v, onV }: { v: RelatieVelden; onV: (v: RelatieVelden) => void }) {
  const [controleert, setControleert] = useState(false)
  /* Wat er gecontroleerd werd; verschilt het van nu, dan is de controle oud. */
  const [gezien, setGezien] = useState(() => JSON.stringify([v.relatie, [...v.divisies].sort()]))
  const verouderd = gezien !== JSON.stringify([v.relatie, [...v.divisies].sort()])
  const ctl = verouderd ? null : v.controle
  const fouten = ctl?.fouten ?? {}
  const r = v.relatie
  const zet = <K extends keyof RelatieGegevens>(k: K, w: RelatieGegevens[K]) => onV({ ...v, relatie: { ...r, [k]: w } })

  /* De kaart zoals hij NU is: wie tijdens het controleren verder typt of een
     bv aanvinkt, mag dat niet kwijtraken aan het antwoord op de oude kaart. */
  const nu = useRef(v)
  nu.current = v

  async function opnieuw() {
    setControleert(true)
    const divisies = [...v.divisies]
    const verstuurd = JSON.stringify(r)
    try {
      const { controle } = await c('relatie-controleren', { relatie: r, divisies })
      const { relatie, ...rest } = controle
      const huidig = nu.current
      const toch = huidig.toch.filter((b) => rest.perBv.some((p) => p.division === b && p.treffers.length))
      /* Niets veranderd intussen: de nette vorm van de server overnemen
         (IBAN zonder spaties, btw in hoofdletters). Wel veranderd: de velden
         laten staan; de controle geldt dan voor de oude stand en staat als
         "verouderd" op het scherm. */
      const ongewijzigd = JSON.stringify(huidig.relatie) === verstuurd
      onV({ ...huidig, relatie: ongewijzigd ? relatie : huidig.relatie, controle: rest, toch })
      setGezien(JSON.stringify([ongewijzigd ? relatie : JSON.parse(verstuurd), divisies.sort()]))
    } catch (e) {
      meldFout(e)
    } finally {
      setControleert(false)
    }
  }

  const tekstveld = (k: keyof RelatieGegevens, label: string, o: { breed?: boolean; mono?: boolean; placeholder?: string } = {}) => (
    <label className={`veld ${o.breed ? 'breed' : ''}`}>
      <span className="veld-label">{label}</span>
      <input className={`invoer ${o.mono ? 'mono' : ''}`} value={String(r[k] ?? '')} placeholder={o.placeholder}
        onChange={(e) => zet(k, e.target.value as never)} aria-invalid={!!fouten[k]} />
      {fouten[k] && <span className="veld-fout" role="alert">{fouten[k]}</span>}
    </label>
  )

  return (
    <div className="formulier compact relatie-kaart">
      {tekstveld('naam', 'Naam', { breed: true })}
      <label className="veld">
        <span className="veld-label">Soort</span>
        <select className="invoer" value={r.soort} onChange={(e) => zet('soort', e.target.value as RelatieGegevens['soort'])}>
          {SOORTEN.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </label>
      {tekstveld('land', 'Land', { mono: true, placeholder: 'NL' })}
      {tekstveld('btw', 'Btw-nummer', { mono: true, placeholder: 'NL123456789B01' })}
      {tekstveld('kvk', 'KvK-nummer', { mono: true })}
      {tekstveld('adres', 'Straat en huisnummer', { breed: true })}
      {tekstveld('postcode', 'Postcode', { mono: true })}
      {tekstveld('plaats', 'Plaats')}
      {tekstveld('email', 'E-mail')}
      {tekstveld('telefoon', 'Telefoon')}
      {tekstveld('website', 'Website', { breed: true })}
      {tekstveld('iban', 'IBAN', { mono: true, breed: true })}
      {tekstveld('bic', 'BIC', { mono: true })}
      {tekstveld('tenaamstelling', 'Op naam van', { placeholder: 'Leeg = de naam' })}
      <label className="veld breed">
        <span className="veld-label">Notitie in Exact</span>
        <textarea className="invoer" rows={2} value={r.notitie} onChange={(e) => zet('notitie', e.target.value)} />
      </label>

      {v.vies && !verouderd && <p className="waarschuwing breed"><AlertTriangle size={13} /> {v.vies}</p>}

      <div className="veld breed">
        <span className="veld-label">In welke bv's ({v.divisies.length})</span>
        <div className="relatie-bvs">
          {v.mogelijk.map((d) => {
            const p = ctl?.perBv.find((x) => x.division === d.code)
            return (
              <div key={d.code} className="relatie-bv">
                <label className="vinkje">
                  <input type="checkbox" checked={v.divisies.includes(d.code)}
                    onChange={(e) => onV({ ...v, divisies: e.target.checked ? [...v.divisies, d.code] : v.divisies.filter((x) => x !== d.code) })} />
                  <span>{d.naam}</span>
                </label>
                {p && <BvStand p={p} toch={v.toch.includes(d.code)}
                  onToch={(aan) => onV({ ...v, toch: aan ? [...v.toch, d.code] : v.toch.filter((x) => x !== d.code) })} />}
              </div>
            )
          })}
        </div>
      </div>

      {ctl && ctl.waarschuwingen.length > 0 && (
        <div className="relatie-waarschuwing breed">
          {ctl.waarschuwingen.map((w) => <p key={w}><AlertTriangle size={13} /> {w}</p>)}
        </div>
      )}
      <div className="breed relatie-opnieuw">
        {verouderd && <span className="stil">Er is iets veranderd; de controle hierboven is van daarvoor.</span>}
        <Knop klein icoon={<RefreshCw size={13} />} bezig={controleert} disabled={!v.divisies.length || !r.naam.trim()} onClick={() => void opnieuw()}>
          Opnieuw controleren
        </Knop>
      </div>
    </div>
  )
}

function BvStand({ p, toch, onToch }: {
  p: RelatieControle['perBv'][number]
  toch: boolean
  onToch: (aan: boolean) => void
}) {
  if (p.fout) return <div className="relatie-stand fout"><XCircle size={12} /> Niet na te kijken: {p.fout}</div>
  if (!p.treffers.length) return <div className="relatie-stand ok"><Check size={12} /> Bestaat hier nog niet</div>
  return (
    <div className="relatie-stand warn">
      {p.treffers.map((t) => (
        <div key={t.id} className={t.verdacht ? 'verdacht' : ''}>
          {t.verdacht ? <ShieldAlert size={12} /> : <AlertTriangle size={12} />}{' '}
          Bestaat al:{' '}
          {t.code
            ? <a href={`${EXACT_URL}/relatie/${p.division}/${encodeURIComponent(t.code)}`} target="_blank" rel="noreferrer">{t.code} {t.naam}</a>
            : t.naam}
          {t.plaats ? `, ${t.plaats}` : ''} <span className="stil">(zelfde {t.op.map((o) => OP[o] ?? o).join(', ')})</span>
          {t.verdacht && <div>Hetzelfde IBAN bij een andere naam: bel de leverancier op een bekend nummer.</div>}
        </div>
      ))}
      <label className="vinkje"><input type="checkbox" checked={toch} onChange={(e) => onToch(e.target.checked)} /> Toch aanmaken in deze bv</label>
    </div>
  )
}
